/**
 * Inventory Integration for Subcontracting
 * Handles inventory transactions and warehouse moves for subcontracting operations
 *
 * Uses the 'inventory_tx' table with the following schema:
 * - txn_type: text (transaction type)
 * - item_id: uuid (references inventory_items.id)
 * - qty: numeric (quantity - negative for issues, positive for receipts)
 * - uom: text (unit of measure)
 * - wh_from: uuid (source warehouse id)
 * - wh_to: uuid (destination warehouse id)
 * - ref_table: text (reference table name)
 * - ref_id: uuid (reference record id)
 * - note: text (notes)
 * - created_by: uuid (user id)
 * - created_at: timestamptz
 */

import { supabase } from '@/lib/supabase/client';
import {
  SubcontractDispatchedEvent,
  SubcontractReceivedEvent,
  subcontractingEvents
} from '@/lib/events/subcontractingEvents';

/**
 * Inventory transaction record matching the inventory_tx table schema
 */
export interface InventoryTx {
  id?: string;
  txn_type: 'SUBCON-ISSUE' | 'SUBCON-RECEIPT' | 'WIP-TO-FG' | 'ISSUE-TO-SUBCON' | 'GRN-RECEIPT' | string;
  item_id?: string; // UUID of bom_components record (legacy)
  inventory_item_id?: string; // UUID of inventory_items record (for stock updates)
  qty: number; // Quantity (always positive, direction determined by txn_type)
  uom: string;
  wh_from?: string; // UUID of source warehouse
  wh_to?: string; // UUID of destination warehouse
  unit_cost?: number;
  ref_table?: string; // Reference table name (e.g., 'sub_contracts')
  ref_id?: string; // UUID of reference record
  note?: string;
  created_by?: string;
  created_at?: string;
  lot_id?: string;
  serial_id?: string;
  bin_from?: string;
  bin_to?: string;
}

// Legacy interface for backward compatibility
export interface InventoryTransaction extends InventoryTx {
  transaction_type?: string;
  reference_number?: string;
  reference_type?: string;
  reference_id?: string;
  item_code?: string;
  quantity?: number;
  from_location?: string;
  to_location?: string;
  notes?: string;
}

export interface SubcontractWarehouseConfig {
  subcontractingWarehouseId?: string; // UUID of subcontracting warehouse (SC-WIP)
  wipLocationId?: string; // UUID of WIP location (WIP-INHOUSE)
  fgLocationId?: string; // UUID of FG location
  mainWarehouseId?: string; // UUID of main warehouse (MAIN)
  qcHoldWarehouseId?: string; // UUID of QC hold warehouse (QC-HOLD)
  scrapWarehouseId?: string; // UUID of scrap warehouse (SCRAP)
}

// Warehouse code to config key mapping
const WAREHOUSE_CODE_MAP: Record<string, keyof SubcontractWarehouseConfig> = {
  'SC-WIP': 'subcontractingWarehouseId',
  'WIP-INHOUSE': 'wipLocationId',
  'MAIN': 'mainWarehouseId',
  'QC-HOLD': 'qcHoldWarehouseId',
  'SCRAP': 'scrapWarehouseId',
};

// Cache for warehouse IDs to avoid repeated lookups
let warehouseCache: SubcontractWarehouseConfig | null = null;
let warehouseCacheTimestamp: number = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL

/**
 * Fetch warehouse IDs from database and cache them
 */
async function fetchWarehouseIds(): Promise<SubcontractWarehouseConfig> {
  const now = Date.now();

  // Return cached values if still valid
  if (warehouseCache && (now - warehouseCacheTimestamp) < CACHE_TTL_MS) {
    return warehouseCache;
  }

  try {
    const { data, error } = await supabase
      .from('warehouse')
      .select('id, code')
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching warehouses:', error);
      return {};
    }

    const config: SubcontractWarehouseConfig = {};

    for (const warehouse of data || []) {
      const configKey = WAREHOUSE_CODE_MAP[warehouse.code];
      if (configKey) {
        config[configKey] = warehouse.id;
      }
    }

    // Update cache
    warehouseCache = config;
    warehouseCacheTimestamp = now;

    console.log('📦 Warehouse IDs loaded:', Object.keys(config).length, 'warehouses configured');

    return config;
  } catch (error) {
    console.error('Error in fetchWarehouseIds:', error);
    return {};
  }
}

/**
 * Clear the warehouse cache (useful when warehouses are updated)
 */
export function clearWarehouseCache(): void {
  warehouseCache = null;
  warehouseCacheTimestamp = 0;
}

class InventoryIntegration {
  private warehouseConfig: SubcontractWarehouseConfig;
  private initialized: boolean = false;

  constructor(config?: Partial<SubcontractWarehouseConfig>) {
    this.warehouseConfig = { ...config };
  }

  /**
   * Initialize warehouse configuration from database
   */
  private async ensureInitialized(): Promise<void> {
    if (this.initialized && Object.keys(this.warehouseConfig).length > 0) {
      return;
    }

    const dbConfig = await fetchWarehouseIds();
    this.warehouseConfig = { ...dbConfig, ...this.warehouseConfig };
    this.initialized = true;
  }

  /**
   * Look up inventory_item ID by item code
   * Returns undefined if not found (inventory_item_id is optional in inventory_tx)
   */
  private async lookupInventoryItemId(itemCode: string): Promise<string | undefined> {
    try {
      const { data } = await supabase
        .from('inventory_items')
        .select('id')
        .eq('code', itemCode)
        .maybeSingle();
      return data?.id;
    } catch (error) {
      console.warn(`Could not look up inventory_item_id for ${itemCode}:`, error);
      return undefined;
    }
  }

  /**
   * Handle subcontract dispatch - Create SUBCON-ISSUE transaction
   */
  async handleSubcontractDispatch(event: SubcontractDispatchedEvent): Promise<void> {
    try {
      // Ensure warehouse IDs are loaded
      await this.ensureInitialized();

      console.log('🏭 Processing inventory for subcontract dispatch:', event.subContractNumber);

      const transactions: InventoryTx[] = [];

      // Create ISSUE-TO-SUBCON transactions for each material item
      for (const item of event.data.materialItems) {
        const inventoryItemId = await this.lookupInventoryItemId(item.itemCode);

        transactions.push({
          txn_type: 'ISSUE-TO-SUBCON',
          inventory_item_id: inventoryItemId,
          qty: item.quantity, // Positive qty, txn_type determines direction
          uom: item.uom,
          wh_from: this.warehouseConfig.mainWarehouseId || this.warehouseConfig.wipLocationId,
          wh_to: this.warehouseConfig.subcontractingWarehouseId,
          ref_table: 'sub_contracts',
          ref_id: event.subContractId,
          note: `Material issued for subcontracting to ${event.vendorName} (SC: ${event.subContractNumber}, Item: ${item.itemCode})`,
          created_at: event.timestamp
        });
      }

      // Insert inventory transactions
      if (transactions.length > 0) {
        const { error } = await supabase
          .from('inventory_tx')
          .insert(transactions);

        if (error) {
          console.error('Error creating inventory transactions:', error);
          throw error;
        }

        console.log(`✅ Created ${transactions.length} ISSUE-TO-SUBCON transactions in inventory_tx (stock auto-updated via trigger)`);
      }

      // Update job card inventory allocation status
      await this.updateJobCardInventoryStatus(event.data.jobCardIds, 'allocated_to_subcontractor');

    } catch (error) {
      console.error('Error in handleSubcontractDispatch:', error);
      throw error;
    }
  }

  /**
   * Handle subcontract receipt - Create SUBCON-RECEIPT and WIP-TO-FG transactions
   */
  async handleSubcontractReceipt(event: SubcontractReceivedEvent): Promise<void> {
    try {
      // Ensure warehouse IDs are loaded
      await this.ensureInitialized();

      console.log('📦 Processing inventory for subcontract receipt:', event.subContractNumber);

      const transactions: InventoryTx[] = [];

      // Create SUBCON-RECEIPT transactions for completed items
      for (const item of event.data.completedItems) {
        const inventoryItemId = await this.lookupInventoryItemId(item.itemCode);

        // Receipt transaction from subcontractor - stock increases at destination
        transactions.push({
          txn_type: 'SUBCON-RECEIPT',
          inventory_item_id: inventoryItemId,
          qty: item.quantity, // Positive qty, stock will increase at wh_to
          uom: item.uom,
          wh_from: this.warehouseConfig.subcontractingWarehouseId,
          wh_to: this.warehouseConfig.mainWarehouseId || this.warehouseConfig.wipLocationId,
          ref_table: 'sub_contracts',
          ref_id: event.subContractId,
          note: `Completed items received from ${event.vendorName} (SC: ${event.subContractNumber}, Item: ${item.itemCode})`,
          created_at: event.timestamp
        });

        // WIP to FG move transaction (optional - only if FG location is configured)
        if (this.warehouseConfig.fgLocationId) {
          transactions.push({
            txn_type: 'WIP-TO-FG',
            inventory_item_id: inventoryItemId,
            qty: item.quantity,
            uom: item.uom,
            wh_from: this.warehouseConfig.wipLocationId,
            wh_to: this.warehouseConfig.fgLocationId,
            ref_table: 'sub_contracts',
            ref_id: event.subContractId,
            note: `Subcontracted work completed - moved to finished goods (SC: ${event.subContractNumber})`,
            created_at: event.timestamp
          });
        }
      }

      // Insert inventory transactions
      if (transactions.length > 0) {
        const { error } = await supabase
          .from('inventory_tx')
          .insert(transactions);

        if (error) {
          console.error('Error creating inventory transactions:', error);
          throw error;
        }

        console.log(`✅ Created ${transactions.length} inventory transactions (RECEIPT + WIP-TO-FG) in inventory_tx (stock auto-updated via trigger)`);
      }

      // Update job card inventory status and quantities
      await this.updateJobCardQuantitiesFromReceipt(event.data.receivedQuantities);
      await this.updateJobCardInventoryStatus(event.data.jobCardIds, 'completed_from_subcontractor');

    } catch (error) {
      console.error('Error in handleSubcontractReceipt:', error);
      throw error;
    }
  }

  /**
   * Update job card inventory allocation status
   */
  private async updateJobCardInventoryStatus(jobCardIds: string[], status: string): Promise<void> {
    try {
      // TODO: Add inventory_status field to job_cards table
      // For now, just log the status update
      console.log(`ℹ️ Would update inventory status to '${status}' for ${jobCardIds.length} job cards`);
      console.log(`ℹ️ (inventory_status field not yet added to job_cards table)`);

      // const { error } = await supabase
      //   .from('job_cards')
      //   .update({
      //     inventory_status: status,
      //     updated_at: new Date().toISOString()
      //   })
      //   .in('id', jobCardIds);

      // if (error) {
      //   console.error('Error updating job card inventory status:', error);
      //   throw error;
      // }

      // console.log(`✅ Updated inventory status for ${jobCardIds.length} job cards`);
    } catch (error) {
      console.error('Error in updateJobCardInventoryStatus:', error);
      throw error;
    }
  }

  /**
   * Update job card quantities from subcontract receipt
   */
  private async updateJobCardQuantitiesFromReceipt(receivedQuantities: Array<{
    jobCardId: string;
    qtyGood: number;
    qtyReject: number;
  }>): Promise<void> {
    try {
      for (const item of receivedQuantities) {
        const { error } = await supabase
          .from('job_cards')
          .update({
            quantity_completed: item.qtyGood, // Fixed: was 'completed_quantity'
            scrap_quantity: item.qtyReject, // Fixed: was 'reject_quantity', using scrap_quantity instead
            updated_at: new Date().toISOString()
          })
          .eq('id', item.jobCardId);

        if (error) {
          console.error(`Error updating quantities for job card ${item.jobCardId}:`, error);
          throw error;
        }
      }

      console.log(`✅ Updated quantities for ${receivedQuantities.length} job cards`);
    } catch (error) {
      console.error('Error in updateJobCardQuantitiesFromReceipt:', error);
      throw error;
    }
  }

  /**
   * Get inventory transactions for a sub-contract
   */
  async getSubcontractInventoryTransactions(subContractId: string): Promise<InventoryTx[]> {
    try {
      const { data, error } = await supabase
        .from('inventory_tx')
        .select('*')
        .eq('ref_id', subContractId)
        .eq('ref_table', 'sub_contracts')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching subcontract inventory transactions:', error);
      return [];
    }
  }
}

// Global inventory integration instance
export const inventoryIntegration = new InventoryIntegration();

// Register event listeners
subcontractingEvents.on('subcon.dispatched', (event) => {
  return inventoryIntegration.handleSubcontractDispatch(event);
});

subcontractingEvents.on('subcon.received', (event) => {
  return inventoryIntegration.handleSubcontractReceipt(event);
});

export default inventoryIntegration;
